import React, { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from 'src/containers';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/reducers';
import {
  fetchOrderTypesSuccess,
  fetchSales,
} from 'src/redux/action/sales.action';
import { useDispatch } from 'react-redux';
import Tickets from 'src/components/tickets';
import {
  clearCurrentOrder,
  fetchDeliveryCharge,
  fetchTaxes,
  fetchUsers,
} from 'src/redux/action/currentOrder.action';
import Service from 'src/setup/network';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { PiCopySimpleLight } from 'react-icons/pi';
import MapContainer from 'src/components/Map';
import { toastAlert } from 'src/utils/toastAlert';
import { HiSearch } from 'react-icons/hi';
import { CurrencyAmount } from 'src/containers/currency';

const OnlineOrder: React.FC = () => {
  const { salesData } = useSelector(({ salesData }: RootState) => salesData);
  const { orders, websiteOrders } = salesData;
  const dispatch = useDispatch();
  const [currentOrder, setCurrentOrder] = useState('');
  const [latitude, setLatitude] = useState<any>(null);
  const [longitude, setLongitude] = useState<any>(null);
  const [filteredText, setFilteredText] = useState('');
  const currentItem = useMemo(() => {
    const item = websiteOrders.find((a: any) => a.id === currentOrder);

    if (
      item?.address?.coordinates?.latitude &&
      item?.address?.coordinates?.longitude
    ) {
      setLatitude(parseFloat(item?.address?.coordinates?.latitude));
      setLongitude(parseFloat(item?.address?.coordinates?.longitude));
    } else {
      setLatitude(null);
      setLongitude(null);
    }
    return item;
  }, [websiteOrders, currentOrder]);
  const navigate = useNavigate();
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [declineLoading, setDeclineLoading] = useState(false);

  const currentItemPromoAmount = useMemo(() => {
    if (!currentItem?.promo || !currentItem) {
      return 0;
    }
    let promoAmount = 0;
    if (currentItem?.promo) {
      if (currentItem.promo?.type === 'percent') {
        promoAmount = Math.ceil(
          (currentItem.promo.value / 100) * currentItem.subTotal
        );
      }
      if (currentItem.promo?.type === 'amount') {
        promoAmount = Math.ceil(currentItem.promo.value);
      }
    }
    return promoAmount;
  }, [websiteOrders, currentItem]);

  const currentItemTaxesAmount = useMemo(() => {
    if (!currentItem) return 0;
    let totalTaxAmount = 0;
    if (currentItem.taxes.length > 0) {
      totalTaxAmount = currentItem.taxes.reduce((acc: any, tax: any) => {
        if (tax.type === 'percent') {
          return acc + Math.ceil((tax.value / 100) * currentItem.subTotal);
        } else if (tax.type === 'amount') {
          return acc + tax.value;
        }
        return acc;
      }, 0);
    }
    return totalTaxAmount;
  }, [websiteOrders, currentItem]);

  useEffect(() => {
    const fetchOrderType = async () => {
      try {
        const res = await Service.get('/businesses/orderTypes');
        dispatch(fetchOrderTypesSuccess(res.data));
      } catch (err: any) {}
    };
    fetchOrderType();
  }, []);

  const copyMapAddress = (lat: string, long: string) => {
    if (!lat || !long) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${long}`;

    navigator.clipboard.writeText(url);
    toastAlert('success', `Copied address`);
  };

  useEffect(() => {
    dispatch(fetchSales() as any);
    dispatch(clearCurrentOrder());
    dispatch(fetchTaxes() as any);
    dispatch(fetchDeliveryCharge() as any);
    dispatch(fetchUsers() as any);
  }, []);

  const changeStatus = async (stat: string) => {
    if (acceptLoading || declineLoading) return;
    if (stat === 'decline') {
      setDeclineLoading(true);
    }
    if (stat === 'accept') {
      setAcceptLoading(true);
    }
    const data = {
      id: currentItem.id,
      newStatus: stat === 'accept' ? 'preparing' : 'failed',
    };
    try {
      await Service.post('/orders/status', data);
      dispatch(fetchSales() as any);
      if (stat === 'decline') {
        setDeclineLoading(false);
      }
      if (stat === 'accept') {
        setAcceptLoading(false);
      }
    } catch (err: any) {
      if (stat === 'decline') {
        setDeclineLoading(false);
      }
      if (stat === 'accept') {
        setAcceptLoading(false);
      }
    }
  };

  const filteredOnlineOrders = useMemo(() => {
    if (websiteOrders.length > 0) {
      if (filteredText.trim().length > 0) {
        const a = websiteOrders.filter((i: any) =>
          i?.referenceNumber
            ?.split('-')[1]
            .startsWith(filteredText.toLowerCase())
        );
        return a;
      } else {
        return websiteOrders;
      }
    } else {
      return [];
    }
  }, [filteredText, websiteOrders]);

  return (
    <>
      <div className="w-full h-[calc(100vh-75px)]  text-black flex flex-row justify-between gap-4">
        <div className="w-full h-full flex   ">
          {websiteOrders.length > 0 ? (
            <div className="flex flex-col gap-3 w-[276px]">
              <div className="relative flex items-center w-[276px] h-[41px]">
                <input
                  type="text"
                  placeholder="Search orders"
                  className="pr-9 flex-1 w-full px-4 py-2 border border-[#CACFE3] rounded-full focus:outline-none "
                  onChange={(e) => {
                    setFilteredText(e.target.value);
                  }}
                />
                <div className="absolute right-3">
                  <HiSearch
                    className="text-gray-400"
                    style={{ fontSize: '20px', color: '#7F8397' }}
                  />
                </div>
              </div>
              <div className="w-[276px] min-w-[276px] h-full overflow-y-auto border rounded-xl border-[#CACFE3]">
                {filteredOnlineOrders.map((item: any, index: number) => {
                  let promoAmount = 0;
                  if (item?.promo) {
                    if (item.promo?.type === 'percent') {
                      promoAmount = Math.ceil(
                        (item.promo.value / 100) * item.subTotal
                      );
                    }
                    if (item.promo?.type === 'amount') {
                      promoAmount = Math.ceil(item.promo.value);
                    }
                  }

                  let totalTaxAmount = 0;
                  if (item.taxes.length > 0) {
                    totalTaxAmount = item.taxes.reduce((acc: any, tax: any) => {
                      if (tax.type === 'percent') {
                        return (
                          acc + Math.ceil((tax.value / 100) * item.subTotal)
                        );
                      } else if (tax.type === 'amount') {
                        return acc + tax.value;
                      }
                      return acc;
                    }, 0);
                  }
                  return (
                    <div
                      key={item.id}
                      className={`w-[276px] h-[76px]  flex flex-col cursor-pointer  py-3 px-4 gap-[6px]  ${
                        currentOrder === item.id ? 'bg-[#D9E0FF] ' : ''
                      } ${
                        index < websiteOrders.length - 1
                          ? 'border-b border-[#F1F0F6]'
                          : ''
                      }`}
                      onClick={() => {
                        if (currentOrder === item.id) {
                          setCurrentOrder('');
                        } else {
                          setCurrentOrder(item.id);
                        }
                      }}
                    >
                      <div
                        className={`w-fit px-2 uppercase  bg-opacity-20 h-[22px] flex items-center justify-center rounded-[5px]  font-Outfit font-normal text-[12px] leading-[18px] ${
                          item.status === 'preparing'
                            ? 'text-[#4062FF] bg-[#4062FF] border-none'
                            : item.status === 'dispatched'
                            ? 'text-[#FEC02D] bg-[#FEC02D] border-none'
                            : item.status === 'cooking'
                            ? 'text-success bg-success border-none'
                            : item.status === 'new'
                            ? 'text-[#7F8397] bg-[#7F839733]'
                            : item.status === 'failed' ||
                              item.status === 'canceled'
                            ? 'text-[#E22B41] bg-[#E22B41] border-none'
                            : item.status === 'delivered'
                            ? 'text-[#0BC988] bg-[#0BC988] border-none'
                            : item.status === 'completed'
                            ? 'text-[#2B2B2B] bg-[#2B2B2B]'
                            : 'text-[#7F839733] bg-[#7F839733]'
                        } w-fit`}
                      >
                        {item.status}
                      </div>
                      <div className="flex flex-row justify-between">
                        <div className="font-Outfit font-semibold text-base text-[#2B2B2B]">
                          Order-{item?.referenceNumber.split('-')[1]}
                        </div>
                        <div className="font-Outfit font-normal text-base text-[#2B2B2B]">
                          <CurrencyAmount amount=
                          {Math.ceil(
                            item.subTotal +
                              totalTaxAmount -
                              promoAmount +
                              item?.deliveryCharge || 0
                          )}/>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {filteredOnlineOrders.length === 0 && (
                  <div className="flex justify-center mt-4">No order found</div>
                )}
              </div>
            </div>
          ) : (
            <div>No online orders.</div>
          )}

          {/* {websiteOrders.length === 0 && <div>No online orders.</div>} */}

          {currentOrder && (
            <div className="w-fit  flex flex-col flex-grow gap-3">
              <div className="ml-6 flex gap-3  h-[60px]  items-center">
                <div className="font-Outfit font-semibold text-base text-[#2B2B2B] ">
                  Order-{currentItem?.referenceNumber.split('-')[1]}
                </div>
                <div
                  className={`w-fit px-2 uppercase  bg-opacity-20 h-[22px] flex items-center justify-center rounded-[5px]  font-Outfit font-normal text-[12px] leading-[18px] ${
                    currentItem.status === 'preparing'
                      ? 'text-[#4062FF] bg-[#4062FF] border-none'
                      : currentItem.status === 'dispatched'
                      ? 'text-[#FEC02D] bg-[#FEC02D] border-none'
                      : currentItem.status === 'cooking'
                      ? 'text-success bg-success border-none'
                      : currentItem.status === 'new'
                      ? 'text-[#7F8397] bg-[#7F839733]'
                      : currentItem.status === 'failed' ||
                        currentItem.status === 'canceled'
                      ? 'text-[#E22B41] bg-[#E22B41] border-none'
                      : currentItem.status === 'delivered'
                      ? 'text-[#0BC988] bg-[#0BC988] border-none'
                      : currentItem.status === 'completed'
                      ? 'text-[#2B2B2B] bg-[#2B2B2B]'
                      : 'text-[#7F839733] bg-[#7F839733]'
                  } w-fit`}
                >
                  {currentItem.status}
                </div>
              </div>
              <div className="flex gap-4  ml-6 flex-grow mb-3 overflow-scroll flex-wrap">
                <div className="w-[350px] flex flex-col gap-4">
                  <div className="border border-[#CACFE3] rounded-xl w-[350px] h-fit p-4 flex flex-col ">
                    <div className="font-Outfit font-semibold text-sm text-[#7F8397] mb-4">
                      Customer details
                    </div>
                    <div className="flex justify-start items-center gap-6 mb-3 ">
                      <div className="font-Outfit font-normal text-sm text-[#7F8397]  w-[60px]">
                        Name
                      </div>
                      <div className="font-Outfit font-normal text-sm text-[#2B2B2B]">
                        {currentItem?.customer?.name}
                      </div>
                    </div>
                    <div className="flex justify-start items-center gap-6 mb-3">
                      <div className="font-Outfit font-normal text-sm text-[#7F8397]  w-[60px]">
                        Email
                      </div>
                      <div className="font-Outfit font-normal text-sm text-[#2B2B2B]">
                        {currentItem?.customer?.email}
                      </div>
                    </div>
                    <div className="flex justify-start items-center gap-6 mb-3">
                      <div className="font-Outfit font-normal text-sm text-[#7F8397]  w-[60px]">
                        phone
                      </div>
                      <div className="font-Outfit font-normal text-sm text-[#2B2B2B]">
                        {currentItem?.customer?.phone}
                      </div>
                    </div>
                    <div className="w-[109px] h-[37px] rounded-xl flex items-center justify-center bg-[#D9E0FF] font-Outfit font-semibold text-sm text-[#4062FF] cursor-pointer">
                      View Profile
                    </div>
                  </div>
                  <div className="border border-[#CACFE3] rounded-xl w-[350px] h-fit p-4 flex flex-col ">
                    <div className="font-Outfit font-semibold text-sm text-[#7F8397] mb-4">
                      Payment Info
                    </div>

                    <div className="flex justify-start items-center gap-6 mb-3">
                      <div className="font-Outfit font-normal text-sm text-[#7F8397] w-[60px]">
                        Total
                      </div>
                      <div className="font-Outfit font-normal text-sm text-[#2B2B2B]">
                        <CurrencyAmount amount=
                        {Math.ceil(
                          currentItem.subTotal +
                            currentItemTaxesAmount -
                            currentItemPromoAmount +
                            currentItem?.deliveryCharge || 0
                        )}/>
                      </div>
                    </div>
                    <div className="flex justify-start items-center gap-6 mb-3">
                      <div className="font-Outfit font-normal text-sm text-[#7F8397] w-[60px]">
                        Method
                      </div>
                      <div className="font-Outfit font-normal text-sm text-[#2B2B2B]">
                        Cash on Delivery
                      </div>
                    </div>
                  </div>
                  <div className="border border-[#CACFE3] rounded-xl w-[350px] h-fit p-4 flex flex-col  ">
                    <div className="font-Outfit  font-semibold flex flex-row justify-between text-sm text-[#7F8397] mb-4 ">
                      <div> Address</div>
                      <div
                        className="flex gap-1 cursor-pointer justify-center items-center font-normal border rounded-[5px] border-[#CACFE3] w-[66px] h-[26px]"
                        onClick={() => {
                          copyMapAddress(
                            currentItem?.address?.coordinates?.latitude,
                            currentItem?.address?.coordinates?.longitude
                          );
                        }}
                      >
                        Copy <PiCopySimpleLight />
                      </div>
                    </div>

                    {currentItem?.address?.area ||
                    currentItem?.address?.city ? (
                      <div className="font-Outfit font-normal text-sm text-[#7F8397] mb-3 flex flex-row">
                        <div>{currentItem.address.area} </div>
                        {currentItem.address.city && (
                          <div>, {currentItem.address.city}</div>
                        )}
                      </div>
                    ) : (
                      <></>
                    )}

                    {latitude && longitude && (
                      <MapContainer
                        showMarker={true}
                        radius={0}
                        hideSearchBar={true}
                        latitude={latitude}
                        setLatitude={setLatitude}
                        longitude={longitude}
                        setLongitude={setLongitude}
                      />
                    )}
                  </div>
                </div>
                <div className="w-fit flex flex-col gap-4 ">
                  <div className="border border-[#CACFE3] rounded-xl w-[350px]  min-h-[459px] max-h-[459px] p-4 flex flex-col  ">
                    <div className="font-Outfit font-semibold text-sm text-[#7F8397] mb-4 flex justify-between ">
                      Order List{' '}
                      {currentItem.status === 'processing' && (
                        <div
                          className="text-[#1F3D9E] cursor-pointer"
                          onClick={() => {
                            navigate('/table', {
                              state: {
                                table: null,
                                type: currentItem.type,
                                isEditing: true,
                                orderId: currentItem.id,
                                isWebsite: true,
                              },
                            });
                          }}
                        >
                          Edit Items
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-3 justify-start items-center flex-grow overflow-auto mb-3   ">
                      {currentItem.ordersItems.length > 0 &&
                        currentItem.ordersItems.map((item: any) => {
                          let priceWithModifiers = 0;

                          if (item.modifiers.length > 0) {
                            item.modifiers.forEach((a: any) => {
                              a.options.forEach((b: any) => {
                                priceWithModifiers += b.price;
                              });
                            });
                          }
                          return (
                            <div
                              className="flex items-center w-full  flex-col"
                              key={item.id}
                            >
                              <div className="flex items-center w-full ">
                                <div className="max-w-[200px] break-words font-Outfit font-semibold text-base">
                                  {item?.hasVariants
                                    ? `${item?.itemName}(${item?.variantName})`
                                    : `${item?.itemName}`}{' '}
                                </div>
                                <div className="ml-[6px] font-Outfit font-normal text-base">
                                  x{item?.quantity}
                                </div>
                                <div className="ml-auto font-Outfit font-normal text-base">
                                  <CurrencyAmount amount=
                                  {Math.ceil(
                                    (item?.discountedPrice +
                                      priceWithModifiers) *
                                      item?.quantity
                                  )}/>
                                </div>
                              </div>
                              {item?.modifiers?.length > 0 ? (
                                <>
                                  {item.modifiers.map((mod: any) => (
                                    <div key={mod.id} className="w-full">
                                      {mod.options.map((option: any) => (
                                        <div
                                          key={option.id}
                                          className="w-full h-[21px] flex items-center justify-between "
                                        >
                                          <div className="font-Outfit font-normal text-[14px] leading-[21px] text-[#949CC0]">
                                            {option.name}
                                          </div>
                                          <div className="font-Outfit font-normal text-[14px] leading-[21px] text-[#949CC0]">
                                            <CurrencyAmount amount={option.price}/>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <></>
                              )}

                              {item?.spiceLevel !== null &&
                              item.spiceLevel !== 'No' ? (
                                <>
                                  <div className="w-full h-[21px] flex justify-between items-center">
                                    <div className="font-Outfit font-normal text-[14px] leading-[21px] text-[#949CC0]">
                                      Spice level
                                    </div>
                                    <div className="font-Outfit font-normal text-[14px] leading-[21px] text-[#949CC0]">
                                      {item?.spiceLevel}
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <></>
                              )}

                              {item?.note ? (
                                <>
                                  <div className="w-full h-fit flex flex-col justify-between items-start">
                                    <div className="w-full font-Sarabun font-normal italic text-[14px] h-fit leading-[21px] text-[#7F8397] break-words">
                                      Note: {item?.note}
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <></>
                              )}
                            </div>
                          );
                        })}
                    </div>
                    <div className="flex mt-auto justify-start items-center gap-2  border-t border-[#F1F0F6] pt-3 flex-col">
                      <div className="flex w-full justify-between">
                        <div className="font-Outfit font-normal text-[16px] leading-[24px] text-[#949CC0]">
                          Subtotal
                        </div>
                        <div className="font-Outfit font-normal text-[16px] leading-[24px] text-[#2B2B2B]">
                          <CurrencyAmount amount={currentItem?.subTotal}/>
                        </div>
                      </div>

                      {currentItem?.promo && currentItem?.promo !== null && (
                        <div className="w-full h-[24px] flex justify-between items-center">
                          <div className="font-Outfit font-normal text-[16px] leading-[24px] text-[#949CC0]">
                            {currentItem?.promo?.name}
                            {currentItem?.promo?.type === 'percent' &&
                              ` (${currentItem?.promo?.value}%)`}
                          </div>
                          <div className="font-Outfit font-normal text-[16px] leading-[24px] text-[#E22B41]">
                            <CurrencyAmount amount={currentItemPromoAmount}/>
                          </div>
                        </div>
                      )}
                      {currentItem.taxes.length > 0 &&
                        currentItem.taxes.map((tax: any, taxIndex: number) => (
                          <div
                            key={taxIndex}
                            className="w-full h-[24px] flex justify-between items-center"
                          >
                            <div className="font-Outfit font-normal text-[16px] leading-[24px] text-[#949CC0]">
                              {tax?.name}
                              {tax?.type === 'percent' && ` (${tax?.value}%)`}
                            </div>
                            <div className="font-Outfit font-normal text-[16px] leading-[24px] text-[#2B2B2B]">
                              <CurrencyAmount amount=
                              {tax?.type === 'percent' ?
                                ` ${Math.ceil(
                                  (tax?.value / 100) * currentItem?.subTotal
                                )}` : '-'}/>
                              {tax?.type === 'amount' && `${tax.value}`}
                            </div>
                          </div>
                        ))}

                      {currentItem?.deliveryCharge &&
                      currentItem?.deliveryCharge > 0 ? (
                        <div className="w-full h-[24px] flex justify-between items-center">
                          <div className="font-Outfit font-normal text-[16px] leading-[24px] text-[#949CC0]">
                            Delivery Charge
                          </div>
                          <div className="font-Outfit font-normal text-[16px] leading-[24px] text-[#2B2B2B]">
                            <CurrencyAmount amount={currentItem?.deliveryCharge}/>
                          </div>
                        </div>
                      ) : (
                        <></>
                      )}
                      <div className="flex w-full justify-between">
                        <div className="font-Outfit font-normal text-[16px] leading-[24px] text-[#949CC0]">
                          Total
                        </div>
                        <div className="font-Outfit font-normal text-[16px] leading-[24px] text-[#2B2B2B]">
                          <CurrencyAmount amount=
                          {Math.ceil(
                            currentItem.subTotal +
                              currentItemTaxesAmount -
                              currentItemPromoAmount +
                              currentItem?.deliveryCharge || 0
                          )}/>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* <div className="bg-warning w-[3000px]">There</div> */}
                </div>
              </div>
              {currentItem.status === 'processing' && (
                <div className="h-[65px] min-h-[65px] mt-auto border-t border-[#CACFE3] flex  gap-4 items-center justify-end">
                  <div
                    className="w-[150px] h-[41px] rounded-lg border border-[#4062FF] text-[#4062FF] cursor-pointer font-Outfit font-semibold text-sm flex items-center justify-center"
                    onClick={() => changeStatus('decline')}
                  >
                    {declineLoading ? (
                      <CircularProgress
                        size={20}
                        style={{ color: '#4062FF' }}
                      />
                    ) : (
                      'Decline'
                    )}
                  </div>
                  <div
                    className="w-[150px] h-[41px] rounded-lg border bg-[#4062FF] text-[#FFF] cursor-pointer font-Outfit font-semibold text-sm flex items-center justify-center"
                    onClick={() => changeStatus('accept')}
                  >
                    {acceptLoading ? (
                      <CircularProgress size={20} style={{ color: '#FFF' }} />
                    ) : (
                      'Accept'
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <Tickets orders={orders} type="dine_in" />
      </div>
    </>
  );
};

export default OnlineOrder;
